import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FilterValueType, UserRole } from "../../services/swagger";
import Dropdown, { DropDownItem } from "./Dropdown";
import {
  HTMLInputTypeAttribute,
  ReactNode,
  useState,
  useTransition,
} from "react";
import useRole from "../../hooks/useRole";
import { capitalizeFirstLetter } from "../../helpers/string";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import FieldLabel from "../atoms/FieldLabel";
import { AntSwitch } from "../atoms/AntSwitch";
import { Bounce } from "react-toastify";
import ColorizeIcon from "@mui/icons-material/Colorize";
import LoadingTextLine from "../atoms/LoadingTextLine";

export interface FormInputProps {
  label: string;
  field: string;
  type: FilterValueType | "chip-list" | "switch" | "color";
  placeholder?: string;
  inputType?: HTMLInputTypeAttribute;
  required?: boolean;
  multiline?: boolean;
  labelFontSize?: number;
  rows?: number;
  role?: UserRole;
  maxRows?: number;
  minRows?: number;
  readOnly?: boolean;
  disabled?: boolean;
  endAdornment?: ReactNode;
  switchLabelPlacement?: "top" | "bottom" | "end" | "start" | undefined;
  onEnter?: () => any;
  list?: DropDownItem[];
  freeSolo?: true;
  onSoloTextChange?: (value: string) => void;
  onChange?: (value: string | number | boolean | string[]) => void;
  value?: string | number | boolean | string[];
  inputMode?:
    | "search"
    | "text"
    | "none"
    | "email"
    | "tel"
    | "url"
    | "numeric"
    | "decimal"
    | undefined;
  loading?: boolean;
  autoFocus?: boolean;
}

export default function FormInput(props: FormInputProps) {
  const { t } = useTranslation("FormInput");
  const role = useRole();
  const [isPending, startTransition] = useTransition();
  const [chipListMenu, setChipListMenu] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState<string | number | boolean>("");
  const [visible, setVisible] = useState(false);

  const booleanList: DropDownItem[] = [
    { name: t("True"), value: true },
    { name: t("False"), value: false },
  ];

  const handleAddChip = () => {
    if (!Array.isArray(props.value)) return;
    if (!value) return;
    if (props.value.includes(value as string)) return;
    props.onChange?.([...props.value, value as string]);
    setValue("");
    setChipListMenu(null);
  };

  const handleDeleteChip = (index: number) => {
    if (!Array.isArray(props.value)) return;
    const copyChipList = [...props.value];
    copyChipList.splice(index, 1);
    props.onChange?.(copyChipList);
  };

  if (props.role && !role(props.role)) {
    if (!props.value) return null;
    if (props.type === "list" || props.type === "id") {
      return (
        <Box width={1}>
          <FieldLabel required={props.required} fontSize={props.labelFontSize}>
            {props.label}
          </FieldLabel>
          {props.loading ? (
            <LoadingTextLine />
          ) : (
            <Typography>
              {props.list?.find((e) => e.value === props.value)?.name || ""}
            </Typography>
          )}
        </Box>
      );
    } else if (props.type === "boolean") {
      return (
        <Box width={1}>
          <FieldLabel required={props.required} fontSize={props.labelFontSize}>
            {props.label}
          </FieldLabel>
          {props.loading ? (
            <LoadingTextLine />
          ) : (
            <Typography>
              {capitalizeFirstLetter(props.value.toString())}
            </Typography>
          )}
        </Box>
      );
    } else if (props.type === "date") {
      return (
        <Box width={1}>
          <FieldLabel required={props.required} fontSize={props.labelFontSize}>
            {props.label}
          </FieldLabel>
          {props.loading ? (
            <LoadingTextLine />
          ) : (
            <Typography>
              {new Date(props.value as string).toLocaleString("tr")}
            </Typography>
          )}
        </Box>
      );
    } else
      return (
        <Box width={1}>
          <FieldLabel required={props.required} fontSize={props.labelFontSize}>
            {props.label}
          </FieldLabel>
          {props.loading ? (
            <LoadingTextLine />
          ) : (
            <Typography>{props.value}</Typography>
          )}
        </Box>
      );
  } else if (props.type === FilterValueType.List)
    return (
      <Box width={1}>
        <FieldLabel required={props.required} fontSize={props.labelFontSize}>
          {props.label}
        </FieldLabel>
        {props.loading ? (
          <LoadingTextLine />
        ) : (
          <Dropdown
            freeSolo={props.freeSolo}
            autoFocus={props.autoFocus}
            endAdornment={props.endAdornment}
            readOnly={props.readOnly}
            unselectedValue={props.required ? undefined : "Any"}
            value={props.list?.find((e) => e.value === props.value) || ""}
            items={props.list || []}
            fullWidth
            size="small"
            placeholder={props.placeholder}
            disabled={props.disabled}
            required={props.required}
            onChange={(e, item?: DropDownItem) => {
              if (props.required && item) props.onChange?.(item.value);
              else if (!props.required) props.onChange?.(item?.value || "");
            }}
          />
        )}
      </Box>
    );
  else if (props.type === FilterValueType.Boolean)
    return (
      <Box width={1}>
        <FieldLabel required={props.required} fontSize={props.labelFontSize}>
          {props.label}
        </FieldLabel>
        {props.loading ? (
          <LoadingTextLine />
        ) : (
          <Dropdown
            autoFocus={props.autoFocus}
            endAdornment={props.endAdornment}
            readOnly={props.readOnly}
            unselectedValue={props.required ? undefined : "Any"}
            value={booleanList?.find((e) => e.value === props.value)}
            items={booleanList}
            fullWidth
            size="small"
            disabled={props.disabled}
            placeholder={props.placeholder}
            required
            onChange={(e, item: DropDownItem) => {
              if (props.required && typeof item?.value === "boolean")
                props.onChange?.(item.value);
              else if (!props.required) props.onChange?.(item?.value);
            }}
          />
        )}
      </Box>
    );
  else if (props.type === "chip-list")
    return (
      <Box width={1}>
        <Menu
          disableAutoFocus
          disableAutoFocusItem
          anchorEl={chipListMenu}
          onClose={() => setChipListMenu(null)}
          onFocus={() => chipListMenu?.focus()}
          open={Boolean(chipListMenu)}
        >
          <MenuItem onClick={handleAddChip}>Add {value}...</MenuItem>
        </Menu>
        <FieldLabel required={props.required} fontSize={props.labelFontSize}>
          {props.label}
        </FieldLabel>
        {props.loading ? (
          <LoadingTextLine />
        ) : (
          <Box
            sx={{
              "& .MuiInputBase-root": {
                flexWrap: "wrap",
                rowGap: 1,
                alignItems: "center",
                alignContent: "flex-start",
                // Each chip row is 24px tall with an 8px gap; the field has 8px
                // padding on each side.
                ...(props.rows
                  ? { height: props.rows * 32 - 8 + 16 }
                  : { maxHeight: 136 }),
                overflowY: "auto",
              },
              "& .MuiInputBase-input": {
                flex: "1 1 160px",
                minWidth: 160,
                width: "auto",
              },
            }}
          >
            <InputBase
              autoFocus={props.autoFocus}
              endAdornment={props.endAdornment}
              size="small"
              placeholder={props.placeholder}
              fullWidth
              value={value}
              disabled={props.disabled}
              required={props.required}
              onKeyDown={(e) => {
                if (e.code === "Enter" && (value as string).length) {
                  e.preventDefault();
                  handleAddChip();
                }
                if (e.code === "Backspace") {
                  if (value === "") {
                    e.preventDefault();
                    handleDeleteChip((props.value as string[]).length - 1);
                  }
                }
              }}
              onChange={(e) => {
                if (!e.target.value) setChipListMenu(null);
                else setChipListMenu(e.target);
                setValue(e.target.value);
              }}
              readOnly={props.readOnly}
              inputMode={props.inputMode}
              startAdornment={(props.value as string[]).map((e, i) => (
                <Chip
                  label={e}
                  key={e}
                  size="small"
                  sx={{
                    mr: 1,
                    userSelect: "text",
                    "& .MuiChip-label": {
                      userSelect: "text",
                      cursor: "text",
                    },
                  }}
                  onMouseDown={(event) => {
                    if (
                      (event.target as HTMLElement).closest?.(".MuiChip-label")
                    ) {
                      event.stopPropagation();
                    }
                  }}
                  onDelete={() => handleDeleteChip(i)}
                />
              ))}
            />
          </Box>
        )}
      </Box>
    );
  else if (props.type === "switch")
    return (
      <Box width={1}>
        {props.loading ? (
          <LoadingTextLine />
        ) : (
          <FormControlLabel
            sx={{ color: "text.primary" }}
            control={
              <AntSwitch
                sx={{ mx: 1.5 }}
                onChange={(e) => props.onChange?.(e.target.checked)}
                value={props.value}
                checked={props.value as boolean}
                defaultChecked={props.value as boolean}
              />
            }
            disabled={props.disabled}
            label={props.label}
            key={props.field}
            value={props.value}
            required={props.required}
            labelPlacement={props.switchLabelPlacement}
          />
        )}
      </Box>
    );
  else if (props.type === "color")
    return (
      <Box width={1}>
        <FieldLabel required={props.required} fontSize={props.labelFontSize}>
          {props.label}
        </FieldLabel>
        {props.loading ? (
          <LoadingTextLine />
        ) : (
          <InputBase
            autoFocus={props.autoFocus}
            size="small"
            placeholder={props.placeholder}
            fullWidth
            required={props.required}
            value={props.value}
            rows={props.rows}
            disabled={props.disabled}
            onKeyDown={(e) => {
              if (e.code === "Enter") props.onEnter?.();
            }}
            // type="text"
            endAdornment={
              <Tooltip title={t("ColorTooltip")}>
                <IconButton component="label" size="small">
                  <ColorizeIcon />
                  <input
                    type="color"
                    value={props.value as string}
                    style={{
                      position: "absolute",
                      opacity: 0,
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                    }}
                    onChange={(e) =>
                      startTransition(() => props.onChange?.(e.target.value))
                    }
                  />
                </IconButton>
              </Tooltip>
            }
            readOnly={props.readOnly}
            onChange={(e) => props.onChange?.(e.target.value)}
          />
        )}
      </Box>
    );
  else
    return (
      <Box width={1}>
        <FieldLabel required={props.required} fontSize={props.labelFontSize}>
          {props.label}
        </FieldLabel>
        {props.loading ? (
          <LoadingTextLine />
        ) : (
          <InputBase
            autoFocus={props.autoFocus}
            size="small"
            placeholder={props.placeholder}
            fullWidth
            required={props.required}
            multiline={props.multiline}
            value={props.value}
            rows={props.rows}
            disabled={props.disabled}
            maxRows={props.maxRows}
            minRows={props.minRows}
            onKeyDown={(e) => {
              if (e.code === "Enter") props.onEnter?.();
            }}
            type={
              (props.inputType === "password"
                ? visible
                  ? "text"
                  : "password"
                : props.inputType) ??
              (props.type === FilterValueType.String ? "search" : props.type)
            }
            inputMode={props.inputMode}
            endAdornment={
              props.endAdornment
                ? props.endAdornment
                : props.inputType === "password" && (
                    <IconButton
                      sx={{ m: 0, p: 0 }}
                      onClick={() => setVisible((v) => !v)}
                    >
                      {visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  )
            }
            readOnly={props.readOnly}
            onChange={(e) => props.onChange?.(e.target.value)}
          />
        )}
      </Box>
    );
}
